<template>
  <div class="row justify-content-between">
    <business-plan-navbar
      :user-id="userId"
    ></business-plan-navbar>

    <div
      v-if="!isMobile"
      :class="{
        'col-lg-4 col-xl-3 mb-4 opacity-on-hover': versionGte(2),
        'col-lg-6': versionLt(2)
      }"
    >
      <b-card no-body class="position-relative mb-3">
        <b-tabs card>
          <b-tab active>
            <template v-slot:title>
              <b-dropdown text="Filter" size="sm" variant="link text-decoration-none p-0" no-caret>
                <template v-slot:button-content>
                  {{ periodLabel }}
                  <i class="fa fa-fw fa-ellipsis-h"></i>
                </template>
                <b-dropdown-item
                  @click.prevent="period = 'day'"
                >
                  {{ getPeriodLabel('day') }}
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="period = 'week'"
                >
                  {{ getPeriodLabel('week') }}
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="period = 'month'"
                >
                  {{ getPeriodLabel('month') }}
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="period = 'year'"
                >
                  {{ getPeriodLabel('year') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
            <legacy-activity-tracker
              :show-info-alert="showInfoAlert"
              :version="version"
              :plan="plan"
              :quota-convos-daily="quotaConvosDaily"
              :period="period"
            ></legacy-activity-tracker>
          </b-tab>
          <b-tab
            title="Tasks"
          >
            <template v-slot:title>
              <b-dropdown text="Filter" size="sm" variant="link text-decoration-none p-0" no-caret>
                <template v-slot:button-content>
                  {{ tasksTitle }}
                  <i class="fa fa-fw fa-ellipsis-h"></i>
                </template>
                <b-dropdown-item
                  @click.prevent="tasksView = 'today'"
                >
                  <i :class="`fa fa-fw ${getTasksViewIcon('today')} mr-1`"></i>
                  Tasks Due Today
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="tasksView = 'upcoming'"
                >
                  <i :class="`fa fa-fw ${getTasksViewIcon('upcoming')} mr-1`"></i>
                  Upcoming Tasks
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="tasksView = 'anytime'"
                >
                  <i :class="`fa fa-fw ${getTasksViewIcon('anytime')} mr-1`"></i>
                  Anytime Tasks
                </b-dropdown-item>
                <!--
                <b-dropdown-item
                  @click.prevent="tasksView = 'complete'"
                >
                  <i :class="`fa fa-fw ${getTasksViewIcon('complete')} mr-1`"></i>
                  Completed Tasks
                </b-dropdown-item>
                -->
              </b-dropdown>
            </template>
            <tasks-card
              v-if="tasksView"
              :nested="true"
              :hide-new-task-button="true"
            >
              <tasks-paginated
                :view="tasksView"
                hide-title
                :autoload="true"
                variant="narrow"
                :page-size="5"
                :using-router="false"
                hide-new-task-button
                :user-id="userId"
              ></tasks-paginated>
            </tasks-card>
          </b-tab>
        </b-tabs>
      </b-card>

      <person
        v-if="canManage && plan.person && plan.person.rosters_id"
        :person="plan.person"
        :person-id="plan.person.rosters_id"
        :shadow="false"
        size="sm"
        class="mb-3"
      >
        <template #view-button>
          <i class="fa fa-dashboard"></i>
        </template>
      </person>

      <campaign
        id="businessplanningtracker-campaigns"
        container="row mb-4"
        headline-size="h4"
        carousel-slot-name="#businessplanningtracker"
      ></campaign>
    </div>

    <div
      v-if="versionGte(2)"
      class="col-lg-4 col-xl-6 px-5 px-lg-0"
    >
      <a
        v-if="needsNextPlan && (!isMobile || focus === 'activity')"
        :href="urls.businessPlan.createNextPlan(plan.user_id)"
        class="mt-3 mt-md-0 mb-3 btn btn-lg btn-block btn-primary"
      >
        Create your plan for next year!
        <b class="ml-2 text-nowrap">
          <span class="d-none d-lg-inline-block mr-1">Get Started</span>
          <i class="fa fa-arrow-right"></i>
        </b>
      </a>
      <campaign
        id="businessplanningtrackeralert-campaigns"
        container="row mb-1 mb-lg-0"
        headline-size="h4"
        variant="legacy-banner"
        carousel-slot-name="#businessplanningtrackeralert"
      ></campaign>
      <video-player
        ref="inspiration"
        class="border-1"
        variant="business-plan"
        :playlist="playlist"
        :autoplay="true"
        :muted="true"
        :controls="true"
      ></video-player>
      <business-plan-activity
        v-if="!isMobile"
        :is-agent="isAgent"
      ></business-plan-activity>
    </div>

    <div
      v-if="!isMobile"
      :class="{
        'col-lg-4 col-xl-3 mb-4 opacity-on-hover': versionGte(2),
        'col-lg-6': versionLt(2)
      }"
    >
      <div class="mb-3">
        <a
          :href="urls.settings.collaborations(plan.user_id)"
          class="btn btn-block btn-white"
        >
          <i class="fa fa-handshake mr-1"></i>
          Add Accountability Partners
        </a>
      </div>

      <b-card no-body>
        <b-tabs card>
          <b-tab
            :title="leaderboardTitle"
            class="px-2 py-0"
          >
            <template v-slot:title>
              <b-dropdown text="Filter" size="sm" variant="link text-decoration-none p-0" no-caret>
                <template v-slot:button-content>
                  <i class="fa fa-fw fa-ellipsis-h mr-1"></i>
                  {{ leaderboardTitle }}
                  <!-- <i :class="`fa fa-fw ${leaderboardViewIcon} ml-2`"></i> -->
                </template>
                <b-dropdown-item
                  @click.prevent="leaderboardView = 'conversations'"
                >
                  <i :class="`fa fa-fw ${getLeaderboardViewIcon('conversations')} mr-1`"></i>
                  Conversations
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="leaderboardView = 'appointments'"
                >
                  <i :class="`fa fa-fw ${getLeaderboardViewIcon('appointments')} mr-1`"></i>
                  Appointments
                </b-dropdown-item>
                <!--
                <b-dropdown-item
                  @click.prevent="leaderboardView = 'income'"
                >
                  <i :class="`fa fa-fw ${getLeaderboardViewIcon('income')} mr-1`"></i>
                  Income Goal
                </b-dropdown-item>
                -->
                <b-dropdown-item
                  @click.prevent="leaderboardView = 'sales'"
                >
                  <i :class="`fa fa-fw ${getLeaderboardViewIcon('sales')} mr-1`"></i>
                  Sales
                </b-dropdown-item>
              </b-dropdown>
            </template>
            <leaderboard-card
              v-model="leaderboardView"
              :inset="true"
              :user-id="userId"
              :year="plan.year"
              :theme="theme"
            ></leaderboard-card>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <div
      v-if="isMobile"
      class="col-12 mb-4"
    >
      <div class="card bg-transparent border-0 position-relative">
        <div :class="[
          'card-body',
          {
            'bg-card': focus !== 'activity' && focus !== 'report',
            'p-1 activity-bg': focus === 'activity',
            'report-bg': focus === 'report'
          }
        ]">

          <legacy-activity-tracker
            v-if="isMobile && focus === 'prospect'"
            :version="version"
            :show-info-alert="showInfoAlert"
            :plan="plan"
            :quota-convos-daily="quotaConvosDaily"
          ></legacy-activity-tracker>

          <tasks-paginated
            v-if="isMobile && focus === 'tasks'"
            :view="tasksView"
            hide-title
            icon="fa-book"
            icon-color-class="text-flat-nephritis"
            :autoload="true"
            variant="narrow"
            :page-size="5"
            :using-router="false"
            :hide-new-task-button="true"
          ></tasks-paginated>

          <div
            v-if="isMobile"
            v-show="focus === 'report'"
          >

            <b-card class="mb-3" no-body>
              <b-tabs card>
                <b-tab
                  title="Goals"
                  class="p-0"
                >

                  <template
                    v-if="versionGte(3)"
                    v-slot:title
                  >
                    <b-dropdown text="Filter" size="sm" variant="link text-decoration-none p-0" no-caret>
                      <template v-slot:button-content>
                        <i class="fa fa-fw fa-ellipsis-h mr-1"></i>
                        {{ goalsTitle }}
                        <!-- <i class="fa fa-fw fa-clock-o ml-2"></i> -->
                      </template>
                      <!--
                      <b-dropdown-item
                        @click.prevent="goalsView = 'today'"
                      >
                        <i :class="`fa fa-fw ${getGoalsViewIcon('today')} mr-1`"></i>
                        Today
                      </b-dropdown-item>
                      -->
                      <b-dropdown-item
                        @click.prevent="goalsView = 'week'"
                      >
                        This Week
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click.prevent="goalsView = 'month'"
                      >
                        This Month
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click.prevent="goalsView = 'year'"
                      >
                        This Year
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click.prevent="goalsView = 'lastyear'"
                      >
                        Last Year
                      </b-dropdown-item>
                    </b-dropdown>
                  </template>

                  <widget
                    v-if="versionGte(2) && versionLt(3)"
                    ref="incomeGoalWidget"
                    v-model="incomeGoalWidgetConfig"
                    :shadow="false"
                    class="mb-3"
                  ></widget>


                  <div
                    v-if="versionGte(3)"
                  >
                    <div class="my-3">
                      <div class="d-flex justify-content-center">
                        <div class="mr-2">
                          <b-form-radio-group
                            v-model="goalsDisplayFormat"
                            size="sm"
                            button-variant="white"
                            buttons
                          >
                            <b-form-radio
                              value="doughnuts"
                            >
                              <i class="fa fa-circle-o-notch fa-fw"></i>
                            </b-form-radio>
                            <b-form-radio
                              value="bullets"
                            >
                              <i class="fa fa-bar-chart fa-fw"></i>
                            </b-form-radio>
                          </b-form-radio-group>
                        </div>
                        <select
                          class="custom-select custom-select-sm col-6"
                          v-model="goalsView"
                        >
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                          <option value="ytd">This Year</option>
                          <option value="lastyear">Last Year</option>
                        </select>
                      </div>

                    </div>

                    <div class="mx-2">
                      <hr class="mb-0">
                    </div>

                    <div
                      v-if="showGoalsAsDoughnuts"
                      class="goals-doughnut-widget"
                    >
                      <div
                        class="doughnut-center"
                      >
                        <img
                          v-if="!goalsTooltipIcon"
                          class="seal"
                          src="https://c21redwood-static.s3.us-east-1.amazonaws.com/images/logo/c21-seal-grey.png"
                        >
                        <i
                          v-if="goalsTooltipIcon"
                          :class="`goal-icon fa ${goalsTooltipIcon} fa-fw`"
                        ></i>
                      </div>
                      <widget
                        type="App\Widgets\BusinessPlan\Goals\ActivityChart"
                        :filter="activityChartFilter"
                      ></widget>
                    </div>

                    <div
                      v-if="showGoalsAsBullets"
                    >
                      <widget
                        type="App\Widgets\BusinessPlan\Goals\Income"
                        :filter="activityChartFilter"
                      ></widget>
                      <div class="px-3">
                        <hr>
                      </div>
                      <widget
                        type="App\Widgets\BusinessPlan\Goals\Conversations"
                        :filter="activityChartFilter"
                      ></widget>
                      <div class="px-3">
                        <hr>
                      </div>
                      <widget
                        type="App\Widgets\BusinessPlan\Goals\ListingAppointments"
                        :filter="activityChartFilter"
                      ></widget>
                      <div class="px-3">
                        <hr>
                      </div>
                      <widget
                        type="App\Widgets\BusinessPlan\Goals\BuyerConsultations"
                        :filter="activityChartFilter"
                      ></widget>
                    </div>

                  </div>

                </b-tab>
              </b-tabs>
            </b-card>

            <b-card no-body>
              <b-tabs card>
                <b-tab
                  title="Leaderboard"
                  class="px-2 py-0"
                >
                  <template v-slot:title>
                    <b-dropdown text="Filter" size="sm" variant="link text-decoration-none p-0" no-caret>
                      <template v-slot:button-content>
                        <i class="fa fa-fw fa-ellipsis-h mr-1"></i>
                        Leaderboard
                        <i :class="`fa fa-fw ${leaderboardViewIcon} ml-2`"></i>
                      </template>
                      <b-dropdown-item
                        @click.prevent="leaderboardView = 'conversations'"
                      >
                        <i :class="`fa fa-fw ${getLeaderboardViewIcon('conversations')} mr-1`"></i>
                        Conversations
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click.prevent="leaderboardView = 'appointments'"
                      >
                        <i :class="`fa fa-fw ${getLeaderboardViewIcon('appointments')} mr-1`"></i>
                        Appointments
                      </b-dropdown-item>
                      <!--
                      <b-dropdown-item
                        @click.prevent="leaderboardView = 'income'"
                      >
                        <i :class="`fa fa-fw ${getLeaderboardViewIcon('income')} mr-1`"></i>
                        Income Goal
                      </b-dropdown-item>
                      -->
                      <b-dropdown-item
                        @click.prevent="leaderboardView = 'sales'"
                      >
                        <i :class="`fa fa-fw ${getLeaderboardViewIcon('sales')} mr-1`"></i>
                        Sales
                      </b-dropdown-item>
                    </b-dropdown>
                  </template>
                  <leaderboard-card
                    :inset="true"
                    :user-id="userId"
                    :year="plan.year"
                    v-model="leaderboardView"
                  ></leaderboard-card>
                </b-tab>
              </b-tabs>
            </b-card>
          </div>
          <business-plan-activity
            v-if="isMobile"
            v-show="focus === 'activity'"
          ></business-plan-activity>
        </div>
      </div>
    </div>

    <div
      v-if="isMobile"
      class="col"
    >
      <div class="px-5 pb-5">
        <a
          :href="urls.businessPlan.showEdit(userId, plan.year)"
          class="btn btn-block btn-secondary text-decoration-none position-relative"
        >
          <i class="fa fa-pencil mr-1"></i>
          Edit Plan
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.opaque-on-hover {
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.goals-doughnut-widget {
  position: relative;
  .doughnut-center {
    position: absolute;
    width: 60px;
    height: 60px;
    top: 50%;
    margin-top: -30px;
    left: 50%;
    margin-left: -30px;
    display: flex;
    align-items: center;
    justify-content: center;
    .seal {
      width: 60px;
      position: relative;
      left: -6px;
    }
    .goal-icon {
      font-size: 3rem;
    }
  }

}
</style>

<script>
import BusinessPlanActivity from '@/components/BusinessPlanActivity'
import BusinessPlanNavbar from '@/components/BusinessPlanNavbar'
import Campaign from '@/components/Campaign'
import LegacyActivityTracker from '@/components/LegacyActivityTracker'
import LeaderboardCard from '@/components/LeaderboardCard'
import TasksCard from '@/components/TasksCard'
import TasksPaginated from '@/components/TasksPaginated'
import VideoPlayer from '@/components/VideoPlayer'
import Widget from '@/components/dashboard/Widget'
import compareVersions from 'compare-versions'
import Person from '@/components/roster/Person'
import { getBusinessPlan, getBusinessPlanVideoFeed, getBusinessPlanIncomeGoal, touchPowerHour, countTasks } from '@/api'
import { get } from 'lodash'
import { isMobile } from '@/lib/utils'
import urls from '@/api/urls'
import $ from 'jquery'

export default {
  name: 'BusinessPlanHome',
  components: {
    Campaign,
    BusinessPlanActivity,
    BusinessPlanNavbar,
    LegacyActivityTracker,
    LeaderboardCard,
    Person,
    TasksCard,
    TasksPaginated,
    VideoPlayer,
    Widget
  },
  props: {
    focus: {
      type: String,
      default: 'prospect'
    },
    showInfoAlert: {
      type: Boolean,
      default: true
    },
    userId: {
      type: [String, Number],
      required: true
    },
    isAgent: {
      type: Boolean,
      default: get(window, 'Redwood.businessPlan.isAgent', true)
    },
    canManage: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      urls,
      needsNextPlan: get(window, 'Redwood.businessPlan.needsNextPlan', false),
      forceVideoId: get(window, 'Redwood.businessPlan.forceVideoId', null),
      plan: {
        id: null,
        log: {},
        person: {},
        user_id: null,
        lead_sources: [],
        quota_convos_daily: null
      },
      period: 'day',
      tasksView: 'today',
      leaderboardView: 'conversations',
      goalsView: 'week',
      goalsDisplayFormat: 'doughnuts',
      goalsTooltipIcon: null,
      activePowerHours: null,
      playlist: [],
      version: get(window, 'Redwood.businessPlan.version', 3),
      incomeGoalWidgetConfig: {
        component: 'Number'
      }
    }
  },
  computed: {
    theme() {
      return window.Redwood.businessPlan.theme
    },
    isMobile() {
      return isMobile()
    },
    periodLabel() {
      return this.getPeriodLabel(this.period)
    },
    tasksViewIcon() {
      return this.getTasksViewIcon(this.tasksView)
    },
    goalsViewIcon() {
      return this.getGoalsViewIcon(this.goalsView)
    },
    leaderboardTitle() {
      return {
        conversations: 'Conversations Leaderboard',
        appointments: 'Appointments Leaderboard',
        sales: 'Sales Leaderboard'
      }[this.leaderboardView]
    },
    goalsTitle() {
      return {
        week: "This Week's Goals",
        month: "This Month's Goals",
        ytd: "This Year's Goals",
        lastyear: "Last Year's Goals"
      }[this.goalsView]
    },
    tasksTitle() {
      return {
        today: "Tasks Due Today",
        upcoming: "Upcoming Tasks",
        anytime: "Anytime Tasks",
        complete: "Completed Tasks"
      }[this.tasksView]
    },
    leaderboardViewIcon() {
      return this.getLeaderboardViewIcon(this.leaderboardView)
    },
    quotaConvosDaily() {
      return this.plan.quota_convos_daily
    },
    showGoalsAsDoughnuts() {
      return this.goalsDisplayFormat === 'doughnuts'
    },
    showGoalsAsBullets() {
      return this.goalsDisplayFormat === 'bullets'
    },
    activityChartFilter() {
      return {
        'App\\Widgets\\Filters\\Users': this.userId,
        'App\\Widgets\\Filters\\RangePeriod': this.goalsView
      }
    }
  },
  watch: {
    'plan.id' () {
      this.loadIncomeGoal()
    }
  },
  async mounted () {
    this.loadPlan()
    this.loadTasks()
    this.loadVideoFeed()

    if (this.versionLt(3)) {
      this.$refs['incomeGoalWidget'].loading(true)
    }

    $(this.$el).mouseout(() => {
      this.goalsTooltipIcon = null
    })
  },
  methods: {
    versionGte (compare) {
      return compareVersions.compare(this.version.toString(), compare.toString(), '>=')
    },
    versionLt (compare) {
      return compareVersions.compare(this.version.toString(), compare.toString(), '<')
    },
    versionLte (compare) {
      return compareVersions.compare(this.version.toString(), compare.toString(), '<=')
    },
    async loadPlan () {
      //let response = await getBusinessPlan(this.userId, new Date().getFullYear())
      let response = await getBusinessPlan(this.userId)
      this.plan = response.data.data
      this.plan.person = response.data.person
    },
    async loadTasks () {
      let response = await countTasks('complete', this.userId)

      /*
      if (response.data.count > 0) {
        this.tasksView = 'complete'
      } else {
        this.tasksView = 'upcoming'
      }
      */
    },
    async loadIncomeGoal () {
      try {
        if (this.versionLt(3)) {
          this.$refs['incomeGoalWidget'].loading(true)
        }

        if (this.versionLt(3)) {
          let response = await getBusinessPlanIncomeGoal(this.userId, this.plan.year)
          this.incomeGoalWidgetConfig = response.data
          this.$refs['incomeGoalWidget'].loading(false)
        }
      } catch (e) {
        if (this.versionLt(3)) {
          this.$refs['incomeGoalWidget'].loading(false)
          this.$refs['incomeGoalWidget'].error = 'foo'
        }
      }
    },
    async loadVideoFeed () {
      try {
        let response = await getBusinessPlanVideoFeed(this.userId, this.plan.year, this.forceVideoId)
        this.playlist = response.data
      } catch (e) {

      }
    },
    getGoalsViewIcon (value) {
      switch(value) {
        default:
          return 'fa-clock-o'
      }
    },
    getPeriodLabel (value) {
      switch(value) {
        case 'day':
          return 'Today'
        case 'week':
          return 'This Week'
        case 'month':
          return 'This Month'
        case 'year':
          return 'This Year'
        default:
          return 'Unknown'
      }
    },
    getTasksViewIcon (value) {
      switch(value) {
        case 'complete':
          return 'fa-book'
        case 'today':
          return 'fa-star'
        case 'upcoming':
          return 'fa-archive'
        case 'anytime':
          return 'fa-th-list'
        default:
          return 'far fa-square'
      }
    },
    getLeaderboardViewIcon (value) {
      switch(value) {
        case 'conversations':
          return 'fa-comments'
        case 'income':
          return 'fa-money'
        case 'sales':
          return 'fa-home'
        case 'appointments':
          return 'fa-calendar'
        default:
          return 'far fa-square'
      }
    }
  }
}
</script>